import type { AutomationCandidateDto, AutomationListDto, MeResponse } from '@process-hub/shared';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { PERFORMER_LABEL } from '../components/Badges';
import { describeError } from '../errors';

/**
 * Automation opportunities (D-029): every task or decision step on a current published version
 * that a person still performs, scored from the evidence the interview captured. Admins propose;
 * the process owner accepts or declines; Admins mark the agent shipped. A step that needs
 * judgment, negotiation or client trust is never listed as a candidate.
 */
export function Automation({ me }: { me: MeResponse }) {
  const [data, setData] = useState<AutomationListDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showLow, setShowLow] = useState(false);

  const load = useCallback(() => {
    api
      .automation()
      .then(setData)
      .catch((e: unknown) => setError(describeError(e)));
  }, []);
  useEffect(load, [load]);

  if (error) return <div className="notice notice--error">{error}</div>;
  if (!data) return <div className="notice">Scoring the published steps.</div>;

  const { counts } = data;
  const visible = data.candidates.filter((c) => showLow || c.status !== 'Identified' || (c.score ?? 0) >= 0.5);

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div className="kpis">
        <div className="kpi kpi--orange">
          <div className="kpi__label">Candidates</div>
          <div className="kpi__value">{counts.candidates}</div>
          <div className="kpi__sub">steps scoring 50 or better · {counts.belowThreshold} below</div>
        </div>
        <div className="kpi kpi--light">
          <div className="kpi__value">{counts.agentSteps}</div>
          <div className="kpi__label">Already performed by agents</div>
          <div className="kpi__sub">across current published versions</div>
        </div>
        <div className="kpi">
          <div className="kpi__value">{counts.keptWithPeople}</div>
          <div className="kpi__label">Kept with people</div>
          <div className="kpi__sub">judgment, negotiation or client trust — never proposed</div>
        </div>
        <div className="kpi kpi--gold">
          <div className="kpi__value">{counts.noEvidence}</div>
          <div className="kpi__label">No evidence yet</div>
          <div className="kpi__sub">ask the owner about rules and data, then re-score</div>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <div className="card__title">Automation opportunities</div>
          <div className="card__meta">
            {me.isAdmin ? 'You propose; the process owner decides.' : 'Your department’s steps. Owners decide on proposals here.'}
            <label style={{ marginLeft: 16 }}>
              <input type="checkbox" checked={showLow} onChange={(e) => setShowLow(e.target.checked)} /> show low scores
            </label>
          </div>
        </div>
        {notice && (
          <div className="notice" style={{ margin: '12px 18px 0' }} role="status">
            {notice}
          </div>
        )}
        {visible.length === 0 ? (
          <div className="notice" style={{ margin: 18 }}>
            No candidates yet. Candidates come from published processes whose interviews captured rule clarity and data availability for their steps.
          </div>
        ) : (
          <table className="grid">
            <thead>
              <tr>
                <th>Step</th>
                <th>Process</th>
                <th>Now</th>
                <th>Score</th>
                <th>Hours / month</th>
                <th>Pattern</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <CandidateRow key={`${c.processId}:${c.logicalId}`} c={c} me={me} onChanged={load} onNotice={setNotice} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

const STATUS_LABEL: Record<AutomationCandidateDto['status'], string> = { Identified: 'Identified', Proposed: 'Awaiting owner', Accepted: 'Accepted', Declined: 'Declined', Shipped: 'Live' };
const STATUS_PILL: Record<AutomationCandidateDto['status'], string> = { Identified: 'Draft', Proposed: 'InApproval', Accepted: 'Published', Declined: 'Stale', Shipped: 'Published' };

function ScoreBar({ score }: { score: number | null }) {
  if (score === null) return <span className="muted">—</span>;
  const pct = Math.round(score * 100);
  return (
    <div title={`${pct} / 100`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 80, height: 8, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: pct >= 70 ? 'var(--blue)' : pct >= 50 ? 'var(--blue-light)' : 'var(--muted)' }} />
      </div>
      <span className="mono">{pct}</span>
    </div>
  );
}

function CandidateRow({ c, me, onChanged, onNotice }: { c: AutomationCandidateDto; me: MeResponse; onChanged: () => void; onNotice: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const run = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onNotice(`${c.stepName}: ${label}.`);
      setDeclining(false);
      setComment('');
      onChanged();
    } catch (e) {
      onNotice(describeError(e, `${label} did not go through.`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <tr>
        <td className="grid__primary">
          <button type="button" className="link-button" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
            {c.stepName}
          </button>
          <div className="muted" style={{ fontSize: 12 }}>
            {c.role}
            {c.systems.length ? ` · ${c.systems.join(', ')}` : ''}
          </div>
        </td>
        <td>
          <Link to={`/processes/${c.slug}`}>{c.processName}</Link>
          <div className="muted" style={{ fontSize: 12 }}>
            {c.departmentName} · v{c.versionLabel}
          </div>
        </td>
        <td>{PERFORMER_LABEL[c.performedBy]}</td>
        <td>
          <ScoreBar score={c.score} />
        </td>
        <td className="mono">{c.hoursPerMonth === null ? '—' : c.hoursPerMonth}</td>
        <td>{c.pattern ?? '—'}</td>
        <td>
          <span className={`pill pill--status-${STATUS_PILL[c.status]}`}>{STATUS_LABEL[c.status]}</span>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {me.isAdmin && (c.status === 'Identified' || c.status === 'Declined') && c.eligibility === 'candidate' && (
            <button type="button" className="button button--secondary" disabled={busy} onClick={() => run('sent to the owner', () => api.proposeAutomation(c.processId, c.logicalId))}>
              Send to owner
            </button>
          )}
          {c.canDecide && !declining && (
            <>
              <button type="button" className="button" disabled={busy} onClick={() => run('accepted', () => api.decideAutomation(c.processId, c.logicalId, 'Accepted', null))}>
                Accept
              </button>{' '}
              <button type="button" className="button button--secondary" disabled={busy} onClick={() => setDeclining(true)}>
                Decline
              </button>
            </>
          )}
          {me.isAdmin && c.status === 'Accepted' && (
            <button type="button" className="button" disabled={busy} onClick={() => run('marked live', () => api.shipAutomation(c.processId, c.logicalId))}>
              Mark live
            </button>
          )}
          {me.isAdmin && c.status === 'Proposed' && (
            <>
              {' '}
              <button type="button" className="link-button" disabled={busy} onClick={() => run('withdrawn', () => api.withdrawAutomation(c.processId, c.logicalId))}>
                Withdraw
              </button>
            </>
          )}
        </td>
      </tr>
      {declining && (
        <tr>
          <td colSpan={8}>
            <div className="field" style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <textarea aria-label={`Why decline ${c.stepName}`} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Why not? The automation team reads this." style={{ flex: 1 }} />
              <button type="button" className="button button--danger" disabled={busy || comment.trim().length < 5} onClick={() => run('declined', () => api.decideAutomation(c.processId, c.logicalId, 'Declined', comment.trim()))}>
                Decline
              </button>
              <button type="button" className="button button--secondary" disabled={busy} onClick={() => setDeclining(false)}>
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
      {open && (
        <tr>
          <td colSpan={8} style={{ background: 'var(--card-bg)' }}>
            <div style={{ display: 'grid', gap: 8, padding: '6px 0' }}>
              {c.suggestion && (
                <div>
                  <strong>Suggestion.</strong> {c.suggestion}
                </div>
              )}
              {c.humanKeeps && (
                <div>
                  <strong>What stays with a person.</strong> {c.humanKeeps}
                </div>
              )}
              <div>
                <strong>Why this score.</strong>
                <ul style={{ margin: '4px 0 0 18px' }}>
                  {c.reasons.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
                {c.confidence !== null && <div className="muted" style={{ fontSize: 12 }}>Evidence captured: {Math.round(c.confidence * 100)}% of the four signals.</div>}
              </div>
              {c.ownerDecision && (
                <div>
                  <strong>Owner’s answer.</strong> {c.ownerDecision}
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
